import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { logo } from '../../images/images.js'

import ContactInformationStep from '../foi/ContactInformationStep'
import RequestDetailsStep from '../foi/RequestDetailsStep'
import ReviewStep from '../foi/ReviewStep'
import FOIConfirmation from '../foi/FOIConfirmation'
import { Logo, PageWithTitle, Header } from '../styles/Containers.js'
import { PageTitle } from '../styles/Text.js'
import { postFOIData } from '../../dataClient/apiCalls.js'
import { config } from '../../config.js'
import { formatFOIForPaymentFromDraft } from '../../util/foiPaymentStateFormatting'
import { getFoiStepperArray, foiRequestorPayloadTooLargeMessage } from '../../util/foiConsts.js'

export const PAYMENT_SNAPSHOT_ERROR_MESSAGE =
  "We couldn't continue to payment. Select Edit Request, check that your contact details and request information are " +
  'complete, then submit again.'

function FOIRequestPage() {
  const navigate = useNavigate()
  const { state } = useLocation()

  const defaultContactInfo = state && state.contactInfo ? state.contactInfo : undefined
  const defaultRequestInfo = state && state.requestInfo ? state.requestInfo : undefined

  const [activeStep, setActiveStep] = useState(defaultContactInfo && defaultRequestInfo ? 2 : 0)
  const [contactInfo, setContactInfo] = useState(defaultContactInfo)
  const [requestInfo, setRequestInfo] = useState(defaultRequestInfo)
  const [secondaryFoiId, setSecondaryFoiId] = useState()

  const [submissionLoading, setSubmissionLoading] = useState(false)
  const [submissionError, setSubmissionError] = useState(false)

  useEffect(() => {
    if (defaultContactInfo && defaultRequestInfo) {
      window.scrollTo(0, 0)
    }
  }, [defaultContactInfo, defaultRequestInfo])

  const onNext = () => setActiveStep(activeStep => activeStep + 1)
  const onBack = () => setActiveStep(activeStep => activeStep - 1)

  function saveContactInfo(data) {
    setContactInfo(data)
    onNext()
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }

  function saveRequestInfo(data, direction) {
    setRequestInfo(data)
    direction === 'back' ? onBack() : onNext()
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }

  function submitFOI() {
    if (!contactInfo || !requestInfo) return

    setSubmissionLoading(true)
    setSubmissionError(false)

    const foiData = { ...requestInfo }

    if (foiData.periodStart) foiData.periodStart = new Date(foiData.periodStart).toISOString()
    if (foiData.periodEnd) foiData.periodEnd = new Date(foiData.periodEnd).toISOString()

    const addresses = foiData.addresses || []
    const thirdPartyFiles = foiData.thirdPartyFiles || []

    delete foiData.thirdPartyFiles
    delete foiData.hasAddresses

    const foiPayload = {
      requestorData: { ...contactInfo.requestor },
      foiData: {
        applicantType: contactInfo.requestor.isBusiness ? 'COMMERCIAL' : 'PERSONAL',
        ...foiData
      }
    }

    const fileData = new FormData()
    fileData.append('payload', JSON.stringify(foiPayload))

    thirdPartyFiles.forEach(file => {
      fileData.append('thirdPartyFiles', file, file.name)
    })

    const addressFilesMeta = []
    addresses.forEach((address, addressIndex) => {
      const { files = [], ...addressInfo } = address || {}
      if (files.length > 0) {
        addressFilesMeta.push({
          addressIndex,
          addressInfo: {
            ...addressInfo,
            files: files.map(f => ({ name: f.name, size: f.size }))
          }
        })
      }
      files.forEach(file => {
        fileData.append('addressFiles', file, file.name)
      })
    })

    fileData.append('addressFilesMeta', JSON.stringify(addressFilesMeta))

    postFOIData(fileData)
      .then(response => {
        const foiId = response.data.data.id
        const foiSecondaryId = response.data.data.secondaryId
        const billId = response.data.data.billId

        setSecondaryFoiId(foiSecondaryId)

        if (config.foi.APPLICATION_FEE && billId) {
          const foiPaymentSnapshot = formatFOIForPaymentFromDraft({
            contactInfo,
            requestInfo,
            id: foiId,
            secondaryId: foiSecondaryId,
            status: 'DRAFT'
          })
          if (!foiPaymentSnapshot) {
            setSubmissionError(PAYMENT_SNAPSHOT_ERROR_MESSAGE)
            return
          }
          navigate(`/foi/${foiId}/payment/${billId}`, {
            state: { foiPaymentSnapshot, contactInfo, requestInfo }
          })
        } else {
          onNext()
        }
      })
      .catch(e => {
        console.error('Failed posting FOI data to server', e)
        if (e.response?.status === 413) {
          setSubmissionError(foiRequestorPayloadTooLargeMessage)
        } else if (e.response?.data?.name === 'FileTypeError') {
          setSubmissionError(e.response.data.message)
        } else {
          setSubmissionError(true)
        }
      })
      .finally(() => setSubmissionLoading(false))
  }

  return (
    <>
      {activeStep < 3 && (
        <PageWithTitle gap="40px">
          <Header>
            <Logo src={logo} />
            <PageTitle>Freedom of Information Request - {getFoiStepperArray(false)[activeStep]}</PageTitle>
          </Header>
          {activeStep === 0 && <ContactInformationStep requestor={contactInfo} onComplete={saveContactInfo} />}
          {activeStep === 1 && (
            <RequestDetailsStep requestDetails={requestInfo} onNavigate={saveRequestInfo} disableDigital={!contactInfo?.requestor?.email} />
          )}
          {activeStep === 2 && (
            <ReviewStep
              request={{ ...contactInfo, ...requestInfo }}
              onBack={onBack}
              onComplete={submitFOI}
              error={submissionError}
              loading={submissionLoading}
            />
          )}
        </PageWithTitle>
      )}
      {activeStep === 3 && secondaryFoiId && <FOIConfirmation secondaryId={secondaryFoiId} />}
    </>
  )
}

export default FOIRequestPage
